import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

export type City = "oakland" | "san_jose";

export const CITY_LABEL: Record<City, string> = {
  oakland: "Oakland",
  san_jose: "San José",
};

type CityContextValue = {
  city: City | null;
  setCity: (c: City) => void;
  clear: () => void;
};

const CityContext = createContext<CityContextValue | undefined>(undefined);

const STORAGE_KEY = "cws.city";

export function CityProvider({ children }: { children: ReactNode }) {
  const [city, setCityState] = useState<City | null>(null);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "oakland" || v === "san_jose") setCityState(v);
    } catch { /* ignore */ }
  }, []);

  const setCity = useCallback((c: City) => {
    setCityState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch { /* ignore */ }
  }, []);

  const clear = useCallback(() => {
    setCityState(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  return (
    <CityContext.Provider value={{ city, setCity, clear }}>
      {children}
    </CityContext.Provider>
  );
}

export function useCity() {
  const ctx = useContext(CityContext);
  if (!ctx) throw new Error("useCity must be used within a CityProvider");
  return ctx;
}
